import type { Primitive } from '@sentry/types'
import type { Extras } from '@sentry/types'

/* eslint-disable no-restricted-imports */
import {
  addBreadcrumb,
  captureException,
  captureMessage,
  init,
  setContext as _setContext,
  setTag,
  setTags,
  setUser,
  withScope,
  browserTracingIntegration,
  replayIntegration,
  addIntegration,
  getReplay,
  replayCanvasIntegration,
  vueIntegration,
} from '@sentry/vue'
import { browserProfilingIntegration } from '@sentry/browser'

import { BUILD, ENVIRONMENT, SENTRY_DSN, isProductionBuild } from './config'
import type { App } from 'vue'
import type { Router } from 'vue-router'

let replayEnabled = true
let sessionRestartIntervalId: ReturnType<typeof setInterval> | null = null
let visibilityChangeHandler: (() => void) | null = null
let isRestarting = false
let flushSequence = 0
let visibilityFlushTimeoutId: ReturnType<typeof setTimeout> | null = null

const safeFlush = async (reason: string): Promise<void> => {
  if (isRestarting || !replayEnabled) {
    return
  }

  const replay = getReplay()
  if (!replay) {
    return
  }

  isRestarting = true
  flushSequence += 1

  try {
    await replay.flush()
    _setContext('replayFlush', {
      type: reason,
      sequence: flushSequence,
    })
  } catch (error) {
    _setContext('replayFlush', {
      type: reason,
      sequence: flushSequence,
      error: error instanceof Error ? error.message : String(error),
    })
    console.error('[Sentry] Failed to flush replay session')
  } finally {
    isRestarting = false
  }
}

const isEnvStaging = ENVIRONMENT === 'staging'
const REPLAY_CONFIG = {
  maskAllText: false,
  blockAllMedia: false,
  block: ['img:not([src$=".svg"])', 'video', 'canvas.main-layer', '.medical-3d-view canvas'],
  networkDetailAllowUrls: [
    /.*v7labs\.com\/api.*\/commands/, // workview command
    /.*v7labs\.com\/api.*\/list_ids/, // fetch workview paginated items
    /.*v7labs\.com\/api.*\/list_all_ids/, // fetch workview items
    /.*v7labs\.com\/.*customers.*/,
    /.*v7labs\.com\/.*teams.*/, // Capture anything on v7labs.com that contains 'teams' (eg. membership, properties, workflows, status_counts)
  ],
  networkCaptureBodies: true,
  unblock: ['.darwin-icon'],
  networkResponseHeaders: ['traceparent'],
  _experiments: {
    traceInternals: true,
  },
}
const REPLAY_CANVAS_CONFIG = {
  quality: 'low' as const,
}

export const toggleReplay = (enable = false, withCanvas = false): void => {
  if (!isProductionBuild()) {
    return
  }
  replayEnabled = enable
  if (enable === false) {
    getReplay()?.stop()
    return
  }

  if (!getReplay()) {
    addIntegration(replayIntegration(REPLAY_CONFIG))
  } else {
    try {
      getReplay()?.start()
    } catch {
      // Ignore - replay may already be started
    }
  }

  if (withCanvas) {
    addIntegration(replayCanvasIntegration(REPLAY_CANVAS_CONFIG))
  }

  if (!withCanvas) {
    replayCanvasIntegration({
      enableManualSnapshot: true,
    })
  }
}

export const setupSentry = (Vue: App<Element>, router: Router): void => {
  if (!isProductionBuild()) {
    return
  }
  const browserIntegration = browserTracingIntegration({
    router,
  })

  init({
    Vue,
    dsn: SENTRY_DSN || '',
    integrations: [
      browserIntegration,
      vueIntegration({
        tracingOptions: {
          // This is default. We had it to true, but this is actually causing UI lockups.
          // We need to instead identify specific components to track and list them here
          trackComponents: false,
        },
      }),
      ...(isEnvStaging ? [browserProfilingIntegration()] : []),
    ],
    replaysSessionSampleRate: isProductionBuild() ? 0.1 : 1.0,
    replaysOnErrorSampleRate: isProductionBuild() ? 1.0 : 0.1,
    tracesSampleRate: isProductionBuild() ? 0.01 : 1.0,
    ...(ENVIRONMENT && { environment: ENVIRONMENT }),
    ...(BUILD && { release: BUILD }),
    ...(isEnvStaging ? { profilesSampleRate: 1 } : {}),
    // Anchored: a bare 'mark' would also match ops like 'bookmark'.
    ignoreSpans: [{ op: /^mark$/ }],
    ignoreErrors: [
      /Loading chunk [0-9]+ failed/,
      /Loading CSS chunk [0-9]+ failed/,
      // Stale hashed chunks after a deploy. The open page stays put; this only
      // drops the Sentry report (Chrome/Edge, Firefox, Safari).
      /Failed to fetch dynamically imported module/i,
      /error loading dynamically imported module/i,
      /Importing a module script failed/i,
      /ResizeObserver loop limit exceeded/,
      /ResizeObserver loop completed with undelivered notifications/,
      /Unable to get FullStory session URL: Unable to get url: No FullStory session URL found/,
      // Replay SDK self-errors on flush failure; SDK retries automatically.
      /Unable to send Replay/,
      // Replay SDK self-errors when Sentry ingest rate-limits (429); not app errors.
      /^Rate limit hit$/,
      /Transport returned status code 429/,
    ],
    beforeSend(event, hint) {
      const error = hint.originalException
      if (
        error instanceof Error &&
        (error.message.includes('Event buffer exceeded maximum size') ||
          error.message.includes('buffer exceeded'))
      ) {
        console.warn('[Sentry] Buffer overflow detected, flushing replay session')

        if (isRestarting) {
          return null
        }

        safeFlush('reactive')
        return null
      }

      return event
    },
  })

  toggleReplay(true, false)

  if (isProductionBuild()) {
    if (sessionRestartIntervalId) {
      clearInterval(sessionRestartIntervalId)
      sessionRestartIntervalId = null
    }

    if (visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', visibilityChangeHandler)
      visibilityChangeHandler = null
    }

    if (visibilityFlushTimeoutId) {
      clearTimeout(visibilityFlushTimeoutId)
      visibilityFlushTimeoutId = null
    }

    const FLUSH_INTERVAL_MS = 10 * 60 * 1000 // 10 minutes

    sessionRestartIntervalId = setInterval(() => {
      safeFlush('interval')
    }, FLUSH_INTERVAL_MS)

    visibilityChangeHandler = (): void => {
      if (document.visibilityState === 'hidden') {
        // Debounce visibility flushes to prevent rapid-fire flushes when switching tabs quickly
        if (visibilityFlushTimeoutId) {
          clearTimeout(visibilityFlushTimeoutId)
        }
        visibilityFlushTimeoutId = setTimeout(() => {
          safeFlush('visibility')
          visibilityFlushTimeoutId = null
        }, 1000) // Wait 1s after tab becomes hidden before flushing
      } else {
        // Cancel pending flush if tab becomes visible again
        if (visibilityFlushTimeoutId) {
          clearTimeout(visibilityFlushTimeoutId)
          visibilityFlushTimeoutId = null
        }
      }
    }

    document.addEventListener('visibilitychange', visibilityChangeHandler)
  }
}

export const reportToSentry = (
  error: Error,
  context: { tags?: { [key: string]: Primitive }; extra: Extras },
): void =>
  withScope((scope) => {
    context.tags && scope.setTags(context.tags)
    scope.setExtras(context.extra)
    captureException(error)
  })

export const setContext = (name: string, context: { [key: string]: unknown } = {}): void => {
  _setContext(name, {
    ...context,
    when: new Date().toISOString(),
  })
}

const composeKeyString = (e: KeyboardEvent): string =>
  `${e.shiftKey ? 'SHIFT+' : ''}${e.ctrlKey ? 'CTRL+' : ''}${e.altKey ? 'ALT+' : ''}${
    e.metaKey ? 'CMD+' : ''
  }${(e.key || e.code)?.toUpperCase()}`

export const trackHotkey = (e: KeyboardEvent, source?: 'hotkeyManager'): void => {
  if (
    e.key === 'Shift' ||
    e.code === 'ShiftLeft' ||
    e.code === 'ShiftRight' ||
    e.key === 'Control' ||
    e.code === 'ControlLeft' ||
    e.code === 'ControlRight' ||
    e.key === 'Alt' ||
    e.code === 'AltLeft' ||
    e.code === 'AltRight' ||
    e.key === 'Meta' ||
    e.code === 'MetaLeft' ||
    e.code === 'MetaRight'
  ) {
    return
  }

  const shortcut = composeKeyString(e)
  const target = e.target
    ? (e.target as Element).className || (e.target as Element).localName
    : undefined
  const type = e.type

  addBreadcrumb({
    category: 'hotkey',
    level: 'info',
    type: 'user',
    data: {
      shortcut,
      target,
      type,
      source,
    },
  })
}

export { addBreadcrumb, captureMessage, captureException, setTag, setTags, setUser, getReplay }
